import { useEffect, useState } from "react";

import {
  ISetWardCustomFieldsRequest,
  IWardCustomField,
} from "../../../../@types/admin/AdminHospitalDetails";
import { IWard } from "../../../../@types/admin/AdminWard";
import {
  ISetWardStandardFieldsRequest,
  IWardStandardField,
  StandardFieldCode,
} from "../../../../@types/StandardField";
import {
  useSetWardCustomFields,
  useSetWardStandardFields,
} from "../../../../hooks/mutations/admin/hospitalDetails";
import {
  useWardCustomFields,
  useWardStandardFields,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FilterButton,
  LoadingOverlay,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../../shared";
import {
  OnboardingFieldCaption,
  OnboardingFieldHint,
  ToggleRow,
} from "./HospitalDetails.styles";

interface IWardCustomFieldsModalProps {
  ward: IWard;
  onDone: () => void;
}

/**
 * EN-5192: the built-in onboarding fields a ward can configure, labelled as the Add Patient form
 * labels them. First name, last name, email, date of birth and sex are absent on purpose — they
 * stay mandatory everywhere.
 */
const STANDARD_FIELD_LABELS: Partial<Record<StandardFieldCode, string>> = {
  HospitalPatientId: "Hospital ID",
  Ethnicity: "Ethnicity",
  Height: "Height",
  PhoneNumber: "Phone number",
};

/**
 * Shown and required on every ward. The API enforces this and fills them in when a save
 * omits them, so they are not listed and never sent.
 */
const ALWAYS_REQUIRED: ReadonlySet<StandardFieldCode> =
  new Set<StandardFieldCode>(["DateOfBirth", "Sex"]);

/** The five per-ward flags, with what each one actually controls. */
const FLAGS: {
  key: keyof Pick<
    IWardCustomField,
    | "isEnabled"
    | "isRequired"
    | "showInAddPatient"
    | "showInSidePanel"
    | "showInPatientList"
  >;
  label: string;
}[] = [
  { key: "isEnabled", label: "Enabled" },
  { key: "isRequired", label: "Required" },
  { key: "showInAddPatient", label: "On add patient" },
  { key: "showInSidePanel", label: "On patient details" },
  { key: "showInPatientList", label: "On patient list" },
];

/**
 * How one ward uses the hospital's custom fields — Retool's
 * `modalFrame_wardOnboardingFields` (EN-5172).
 *
 * ⚠️ `PUT /Retool/CustomFields/Wards/{wardId}` replaces the ward's whole
 * configuration; a definition omitted from the payload loses its settings. So
 * every definition is always sent, whether or not the operator touched it.
 *
 * `isRequired` only means anything when `isEnabled` is on, and a field that is
 * required but shown nowhere cannot be filled in — both are called out rather
 * than left as five independent checkboxes, which is how Retool had them.
 *
 * EN-5192 added the built-in onboarding fields above the custom ones. They are the
 * same question for the operator ("which fields does this ward ask for, and which
 * are mandatory?"), so they live on one screen, but they are a separate endpoint
 * and save independently.
 */
export function WardCustomFieldsModal({
  ward,
  onDone,
}: IWardCustomFieldsModalProps) {
  const wardId = Number(ward.id);
  const { showToast } = useToast();
  const { wardCustomFields, isLoading } = useWardCustomFields(wardId);
  const { setWardCustomFields, isSavingWardCustomFields } =
    useSetWardCustomFields();

  const { wardStandardFields, isLoading: isLoadingStandardFields } =
    useWardStandardFields(wardId);
  const { setWardStandardFields, isSavingWardStandardFields } =
    useSetWardStandardFields();

  const [fields, setFields] = useState<IWardCustomField[]>([]);
  const [standardFields, setStandardFields] = useState<IWardStandardField[]>(
    []
  );

  useEffect(() => {
    setFields(wardCustomFields);
  }, [wardCustomFields]);

  useEffect(() => {
    setStandardFields(
      wardStandardFields.filter((field) => !ALWAYS_REQUIRED.has(field.code))
    );
  }, [wardStandardFields]);

  const toggleStandard = (
    code: StandardFieldCode,
    key: "enabled" | "required",
    value: boolean
  ) =>
    setStandardFields((previous) =>
      previous.map((field) =>
        field.code === code ? { ...field, [key]: value } : field
      )
    );

  const toggle = (
    definitionId: string,
    key: (typeof FLAGS)[number]["key"],
    value: boolean
  ) =>
    setFields((previous) =>
      previous.map((field) =>
        field.customFieldDefinitionId === definitionId
          ? { ...field, [key]: value }
          : field
      )
    );

  /** Required but invisible: the answer can never be supplied. */
  const unfillableRequired = fields.filter(
    (field) =>
      field.isEnabled &&
      field.isRequired &&
      !field.showInAddPatient &&
      !field.showInSidePanel
  );

  const handleSave = async () => {
    const request: ISetWardCustomFieldsRequest = {
      // Every definition, not just the changed ones — the endpoint replaces the
      // set.
      fields: fields.map((field) => ({
        customFieldDefinitionId: field.customFieldDefinitionId,
        isEnabled: field.isEnabled,
        // A disabled field's other flags are meaningless; normalised so the
        // stored row cannot say "not enabled but required".
        isRequired: field.isEnabled && field.isRequired,
        showInAddPatient: field.isEnabled && field.showInAddPatient,
        showInSidePanel: field.isEnabled && field.showInSidePanel,
        showInPatientList: field.isEnabled && field.showInPatientList,
        displayOrder: field.displayOrder,
      })),
    };

    const standardRequest: ISetWardStandardFieldsRequest = {
      // Every listed code, not just the changed ones — the endpoint replaces the set
      // (the always-required ones it fills in itself).
      fields: standardFields.map((field) => ({
        code: field.code,
        enabled: field.enabled,
        // A hidden field cannot be answered, so it is never stored as required.
        required: field.enabled && field.required,
      })),
    };

    try {
      // Sequential, not Promise.all: two writes to the same ward, and a failure part-way
      // through should leave a message the operator can act on rather than a race.
      await setWardStandardFields({ wardId, request: standardRequest });

      if (fields.length > 0) {
        await setWardCustomFields({ wardId, request });
      }

      showToast("The ward's onboarding fields were saved.", "success");
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error saving the ward's onboarding fields."
        ),
        "warning"
      );
    }
  };

  const isSaving = isSavingWardCustomFields || isSavingWardStandardFields;

  return (
    <ModalContainer>
      <ModalTitle>Onboarding fields — {ward.name}</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Which fields this ward asks for when a patient is added, and which are
          mandatory. First name, last name, email, date of birth and sex are
          always required and are not listed.
        </ModalBodyText>

        {isLoadingStandardFields && (
          <LoadingOverlay>Loading onboarding fields...</LoadingOverlay>
        )}

        {!isLoadingStandardFields &&
          standardFields.map((field) => (
            <div key={field.code}>
              <OnboardingFieldCaption>
                {STANDARD_FIELD_LABELS[field.code] ?? field.code}
              </OnboardingFieldCaption>
              <ToggleRow>
                <CheckboxField>
                  <input
                    type="checkbox"
                    checked={field.enabled}
                    onChange={(e) =>
                      toggleStandard(field.code, "enabled", e.target.checked)
                    }
                  />
                  Shown
                </CheckboxField>
                <CheckboxField>
                  <input
                    type="checkbox"
                    checked={field.required}
                    // Required means nothing for a field the form does not show.
                    disabled={!field.enabled}
                    onChange={(e) =>
                      toggleStandard(field.code, "required", e.target.checked)
                    }
                  />
                  Required
                </CheckboxField>
              </ToggleRow>
            </div>
          ))}

        <ModalBodyText>
          Which of the hospital&apos;s custom fields this ward uses, and where
          they appear.
        </ModalBodyText>

        {isLoading && <LoadingOverlay>Loading custom fields...</LoadingOverlay>}

        {!isLoading && fields.length === 0 && (
          <OnboardingFieldHint>
            This hospital has no custom fields yet. Add them on the Custom
            fields tab first.
          </OnboardingFieldHint>
        )}

        {fields.map((field) => (
          <div key={field.customFieldDefinitionId}>
            <OnboardingFieldCaption>
              {field.label} · {field.key} · {field.type}
            </OnboardingFieldCaption>
            <ToggleRow>
              {FLAGS.map((flag) => (
                <CheckboxField key={flag.key}>
                  <input
                    type="checkbox"
                    checked={field[flag.key]}
                    // The four placement flags only mean anything for an
                    // enabled field.
                    disabled={flag.key !== "isEnabled" && !field.isEnabled}
                    onChange={(e) =>
                      toggle(
                        field.customFieldDefinitionId,
                        flag.key,
                        e.target.checked
                      )
                    }
                  />
                  {flag.label}
                </CheckboxField>
              ))}
            </ToggleRow>
          </div>
        ))}

        {unfillableRequired.length > 0 && (
          <WarningNotice>
            {unfillableRequired.map((field) => field.label).join(", ")}{" "}
            {unfillableRequired.length === 1 ? "is" : "are"} required but shown
            neither on the add-patient form nor on patient details, so nobody
            can supply the answer.
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            // The standard fields always exist, so the save is no longer gated on the hospital
            // having defined a custom field.
            disabled={isSaving || standardFields.length === 0}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save onboarding fields"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
